import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { logger } from '@/utils/logger'

interface TemplateField {
  id: string
  label: string
  labelVi: string
  labelEn: string
  enabled: boolean
  required: boolean
  order: number
}

interface CytologyTemplateConfig {
  hospitalName: string
  departmentName: string
  headerText: string
  footerText: string
  hospitalAddress: string
  logoUrl: string
  // Default patient info
  defaultSpecimenType: string
  defaultGender: string
  // Organisms
  organisms: TemplateField[]
  defaultOrganisms: Record<string, boolean>
  // Squamous Cells
  squamousCells: TemplateField[]
  defaultSquamousCells: Record<string, boolean>
  // Glandular Cells
  glandularCells: TemplateField[]
  defaultGlandularCells: Record<string, boolean>
  // Other Findings
  otherFindings: TemplateField[]
  defaultOtherFindings: Record<string, boolean>
  // Conclusion and Recommendation
  defaultConclusion: string
  defaultRecommendation: string
}

const CytologyTemplateEditor: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [config, setConfig] = useState<CytologyTemplateConfig>({
    hospitalName: 'SỞ Y TẾ THÁI NGUYÊN',
    departmentName: 'Khoa Giải phẫu Bệnh',
    headerText: 'KẾT QUẢ XÉT NGHIỆM TẾ BÀO HỌC SÀNG LỌC UNG THƯ CỔ TỬ CUNG',
    footerText: 'Bethesda 2014',
    hospitalAddress: 'Thịnh Đán, Thành phố Thái Nguyên, Thái Nguyên',
    logoUrl: '🏥',
    defaultSpecimenType: 'Phết nhúng dịch',
    defaultGender: 'Nữ',
    organisms: [
      { id: 'trichomonas', label: 'Trichomonas vaginalis', labelVi: 'Trichomonas vaginalis', labelEn: 'Trichomonas vaginalis', enabled: true, required: false, order: 1 },
      { id: 'candida', label: 'Nấm Candida', labelVi: 'Nấm Candida', labelEn: 'Candida species', enabled: true, required: false, order: 2 },
      { id: 'bacterialVaginosis', label: 'Viêm âm đạo do vi khuẩn', labelVi: 'Viêm âm đạo do vi khuẩn', labelEn: 'Bacterial vaginosis', enabled: true, required: false, order: 3 },
      { id: 'actinomyces', label: 'Actinomyces', labelVi: 'Actinomyces', labelEn: 'Actinomyces', enabled: true, required: false, order: 4 },
      { id: 'hsv', label: 'HSV', labelVi: 'Virus herpes simplex (HSV)', labelEn: 'Herpes simplex virus (HSV)', enabled: true, required: false, order: 5 },
      { id: 'hpv', label: 'HPV', labelVi: 'Virus u nhú ở người (HPV)', labelEn: 'Human papillomavirus (HPV)', enabled: true, required: false, order: 6 },
    ],
    defaultOrganisms: {
      trichomonas: false,
      candida: false,
      bacterialVaginosis: false,
      actinomyces: false,
      hsv: false,
      hpv: false
    },
    squamousCells: [
      { id: 'ascus', label: 'ASC-US', labelVi: 'Tế bào vảy không điển hình không rõ ý nghĩa (ASC-US)', labelEn: 'Atypical squamous cells of undetermined significance (ASC-US)', enabled: true, required: false, order: 1 },
      { id: 'asch', label: 'ASC-H', labelVi: 'Tế bào vảy không điển hình không loại trừ HSIL (ASC-H)', labelEn: 'Atypical squamous cells cannot exclude HSIL (ASC-H)', enabled: true, required: false, order: 2 },
      { id: 'lsil', label: 'LSIL', labelVi: 'Tổn thương trong biểu mô độ thấp (LSIL)', labelEn: 'Low grade squamous intraepithelial lesion (LSIL)', enabled: true, required: false, order: 3 },
      { id: 'lsilHpv', label: 'LSIL với HPV', labelVi: 'LSIL với hiệu ứng HPV', labelEn: 'LSIL with HPV effect', enabled: true, required: false, order: 4 },
      { id: 'hsil', label: 'HSIL', labelVi: 'Tổn thương trong biểu mô độ cao (HSIL)', labelEn: 'High grade squamous intraepithelial lesion (HSIL)', enabled: true, required: false, order: 5 },
      { id: 'squamousCarcinoma', label: 'Ung thư biểu mô vảy', labelVi: 'Ung thư biểu mô vảy', labelEn: 'Squamous cell carcinoma', enabled: true, required: false, order: 6 },
    ],
    defaultSquamousCells: {
      ascus: false,
      asch: false,
      lsil: false,
      lsilHpv: false,
      hsil: false,
      squamousCarcinoma: false
    },
    glandularCells: [
      { id: 'agc', label: 'AGC', labelVi: 'Tế bào tuyến không điển hình (AGC)', labelEn: 'Atypical glandular cells (AGC)', enabled: true, required: false, order: 1 },
      { id: 'ais', label: 'AIS', labelVi: 'Ung thư tuyến tại chỗ ống cổ tử cung (AIS)', labelEn: 'Endocervical adenocarcinoma in situ (AIS)', enabled: true, required: false, order: 2 },
      { id: 'endocervicalAdenocarcinoma', label: 'Ung thư tuyến ống cổ tử cung', labelVi: 'Ung thư tuyến ống cổ tử cung', labelEn: 'Endocervical adenocarcinoma', enabled: true, required: false, order: 3 },
      { id: 'endometrialAdenocarcinoma', label: 'Ung thư tuyến nội mạc tử cung', labelVi: 'Ung thư tuyến nội mạc tử cung', labelEn: 'Endometrial adenocarcinoma', enabled: true, required: false, order: 4 },
      { id: 'adenocarcinomaNos', label: 'Ung thư tuyến NOS', labelVi: 'Ung thư tuyến không đặc hiệu (NOS)', labelEn: 'Adenocarcinoma NOS', enabled: true, required: false, order: 5 },
      { id: 'adenocarcinomaExtrauterine', label: 'Ung thư tuyến ngoài tử cung', labelVi: 'Ung thư tuyến ngoài tử cung', labelEn: 'Extrauterine adenocarcinoma', enabled: true, required: false, order: 6 },
    ],
    defaultGlandularCells: {
      agc: false,
      ais: false,
      endocervicalAdenocarcinoma: false,
      endometrialAdenocarcinoma: false,
      adenocarcinomaNos: false,
      adenocarcinomaExtrauterine: false
    },
    otherFindings: [
      { id: 'normalCells', label: 'Tế bào bình thường', labelVi: 'Tế bào vảy và tuyến bình thường', labelEn: 'Normal squamous and glandular cells', enabled: true, required: false, order: 1 },
      { id: 'inflammation', label: 'Viêm', labelVi: 'Thay đổi tế bào phản ứng liên quan đến viêm', labelEn: 'Reactive cellular changes associated with inflammation', enabled: true, required: false, order: 2 },
      { id: 'radiation', label: 'Xạ trị', labelVi: 'Thay đổi tế bào phản ứng liên quan đến xạ trị', labelEn: 'Reactive cellular changes associated with radiation', enabled: true, required: false, order: 3 },
      { id: 'iud', label: 'Vòng tránh thai', labelVi: 'Thay đổi tế bào phản ứng liên quan đến vòng tránh thai (IUD)', labelEn: 'Reactive cellular changes associated with IUD', enabled: true, required: false, order: 4 },
      { id: 'atrophy', label: 'Teo kèm viêm', labelVi: 'Teo kèm viêm', labelEn: 'Atrophy with inflammation', enabled: true, required: false, order: 5 },
      { id: 'postHysterectomy', label: 'Sau cắt tử cung', labelVi: 'Tế bào tuyến sau cắt tử cung', labelEn: 'Glandular cells status post hysterectomy', enabled: true, required: false, order: 6 },
      { id: 'endometrialCells', label: 'Tế bào nội mạc tử cung', labelVi: 'Tế bào nội mạc tử cung ở phụ nữ ≥45 tuổi', labelEn: 'Endometrial cells in woman ≥45 years', enabled: true, required: false, order: 7 },
    ],
    defaultOtherFindings: {
      normalCells: true,
      inflammation: false,
      radiation: false,
      iud: false,
      atrophy: false,
      postHysterectomy: false,
      endometrialCells: false
    },
    defaultConclusion: 'TẾ BÀO BIỂU MÔ CỔ TỬ CUNG - ÂM ĐẠO TRONG GIỚI HẠN BÌNH THƯỜNG (NEGATIVE FOR INTRAEPITHELIAL LESION OR MALIGNANCY)',
    defaultRecommendation: 'Kết hợp kết quả xét nghiệm HPV theo dõi theo khuyến cáo.',
  })

  // Load default template from API
  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const response = await fetch('/api/v1/pathology/cytology-templates/default/')
        if (response.ok) {
          const template = await response.json()
          logger.debug('✅ Template config loaded from API:', template.config)

          // Merge with default config to ensure all required fields exist
          if (template.config && Object.keys(template.config).length > 0) {
            setConfig({
              ...config, // Start with defaults
              ...template.config, // Override with saved config
              // Ensure critical fields are never undefined
              organisms: template.config.organisms || config.organisms,
              squamousCells: template.config.squamousCells || config.squamousCells,
              glandularCells: template.config.glandularCells || config.glandularCells,
              otherFindings: template.config.otherFindings || config.otherFindings,
              defaultOrganisms: template.config.defaultOrganisms || config.defaultOrganisms,
              defaultSquamousCells: template.config.defaultSquamousCells || config.defaultSquamousCells,
              defaultGlandularCells: template.config.defaultGlandularCells || config.defaultGlandularCells,
              defaultOtherFindings: template.config.defaultOtherFindings || config.defaultOtherFindings
            })
          } else {
            logger.debug('Empty config from API, using built-in defaults')
          }
        } else {
          logger.debug('No default template found, using built-in defaults')
        }
      } catch (error) {
        console.error('Error loading template from API:', error)
      }
    }

    fetchTemplate()
  }, [])

  const handleSave = async () => {
    logger.debug('💾 Saving template config:', config)

    try {
      // Try to update existing default template, or create new one
      const response = await fetch('/api/v1/pathology/cytology-templates/default/')
      let templateId = null

      if (response.ok) {
        const existingTemplate = await response.json()
        templateId = existingTemplate.id
      }

      // Update or create template
      const saveResponse = await fetch(
        templateId
          ? `/api/v1/pathology/cytology-templates/${templateId}/`
          : '/api/v1/pathology/cytology-templates/',
        {
          method: templateId ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: 'Default Cytology Template',
            description: 'System default cytology report template',
            config: config,
            is_active: true,
            is_default: true
          })
        }
      )

      if (saveResponse.ok) {
        logger.debug('✅ Saved to database')
        toast.success('Đã lưu cấu hình template thành công!')
      } else {
        const error = await saveResponse.json()
        console.error('❌ Failed to save:', error)
        toast.error('Lỗi khi lưu template: ' + JSON.stringify(error))
      }
    } catch (error) {
      console.error('❌ Error saving template:', error)
      toast.error('Lỗi khi lưu template')
    }
  }

  const handleReset = async () => {
    if (window.confirm('Bạn có chắc muốn reset về cấu hình mặc định?')) {
      // Delete current default template
      try {
        const response = await fetch('/api/v1/pathology/cytology-templates/default/')
        if (response.ok) {
          const template = await response.json()
          await fetch(`/api/v1/pathology/cytology-templates/${template.id}/`, {
            method: 'DELETE'
          })
        }
        window.location.reload()
      } catch (error) {
        console.error('Error resetting template:', error)
        toast.error('Lỗi khi reset template')
      }
    }
  }

  const toggleField = (category: 'organisms' | 'squamousCells' | 'glandularCells' | 'otherFindings', fieldId: string) => {
    const categoryFields = config[category]
    const updatedFields = categoryFields.map(f =>
      f.id === fieldId ? { ...f, enabled: !f.enabled } : f
    )
    setConfig({ ...config, [category]: updatedFields })
  }

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Vui lòng chọn file hình ảnh (PNG, JPG, GIF)')
      return
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Kích thước file phải nhỏ hơn 2MB')
      return
    }

    // Convert to base64
    const reader = new FileReader()
    reader.onload = (event) => {
      const base64 = event.target?.result as string
      setConfig({ ...config, logoUrl: base64 })
      toast.success('Đã tải logo lên thành công!')
    }
    reader.onerror = () => {
      toast.error('Lỗi khi đọc file')
    }
    reader.readAsDataURL(file)
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Action Bar */}
        <div className="bg-white p-4 rounded-lg shadow-sm mb-6 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-600 hover:text-gray-800"
            >
              ← {t('common.back')}
            </button>
            <div>
              <h1 className="text-xl font-bold">Chỉnh sửa Template Cytology</h1>
              <p className="text-sm text-gray-600">Cấu hình thông tin mặc định cho báo cáo</p>
            </div>
          </div>

          <div className="flex space-x-2">
            <button
              onClick={handleReset}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300"
            >
              🔄 Reset
            </button>
            <button
              onClick={handleSave}
              className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700"
            >
              💾 Lưu Template
            </button>
          </div>
        </div>

        {/* Template Form */}
        <div className="bg-white p-8 rounded-lg shadow-lg">
          {/* Hospital Header Configuration */}
          <div className="text-center mb-6 border-b pb-4">
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Tên bệnh viện</label>
              <input
                type="text"
                value={config.hospitalName}
                onChange={(e) => setConfig({ ...config, hospitalName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-center font-bold text-lg"
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Tên khoa</label>
              <input
                type="text"
                value={config.departmentName}
                onChange={(e) => setConfig({ ...config, departmentName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-center font-semibold"
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Tiêu đề header</label>
              <input
                type="text"
                value={config.headerText}
                onChange={(e) => setConfig({ ...config, headerText: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-center text-sm"
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Địa chỉ bệnh viện</label>
              <input
                type="text"
                value={config.hospitalAddress}
                onChange={(e) => setConfig({ ...config, hospitalAddress: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-center text-sm"
                placeholder="Ví dụ: Thịnh Đán, Thành phố Thái Nguyên, Thái Nguyên"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Logo (Emoji, URL hoặc Upload)</label>

              {/* Logo Preview */}
              <div className="flex justify-center mb-3">
                <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center border-2 border-gray-300">
                  {config.logoUrl && (config.logoUrl.startsWith('data:image') || config.logoUrl.startsWith('http')) ? (
                    <img src={config.logoUrl} alt="Logo Preview" className="w-full h-full object-contain rounded-full" />
                  ) : (
                    <span className="text-4xl">{config.logoUrl || '🏥'}</span>
                  )}
                </div>
              </div>

              {/* Text Input */}
              <input
                type="text"
                value={config.logoUrl}
                onChange={(e) => setConfig({ ...config, logoUrl: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-center mb-2"
                placeholder="🏥 hoặc https://example.com/logo.png"
              />

              {/* Upload Button */}
              <div className="flex justify-center">
                <label className="cursor-pointer bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md inline-flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Tải ảnh lên
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                </label>
              </div>

              <p className="text-xs text-gray-500 mt-2 text-center">
                Nhập emoji (🏥 ⚕️ 🔬), URL hình ảnh, hoặc tải file lên (PNG, JPG, max 2MB)
              </p>
            </div>
          </div>

          {/* Patient Information Defaults */}
          <div className="border-b pb-6 mb-6">
            <h2 className="text-lg font-bold mb-4">Thông tin bệnh nhân - Giá trị mặc định</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Loại mẫu bệnh phẩm:</label>
                <input
                  type="text"
                  value={config.defaultSpecimenType}
                  onChange={(e) => setConfig({ ...config, defaultSpecimenType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Giới tính mặc định:</label>
                <input
                  type="text"
                  value={config.defaultGender}
                  onChange={(e) => setConfig({ ...config, defaultGender: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>
          </div>

          {/* Bethesda 2014 Results Configuration */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            {/* Organisms */}
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-bold text-green-800 mb-3">{t('cytology.organismsTitle')}</h3>
              <div className="space-y-2">
                {config.organisms?.map(field => (
                  <label key={field.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={config.defaultOrganisms[field.id] || false}
                      onChange={(e) => setConfig({
                        ...config,
                        defaultOrganisms: { ...config.defaultOrganisms, [field.id]: e.target.checked }
                      })}
                      disabled={!field.enabled}
                      className="w-4 h-4"
                    />
                    <span className={`text-sm ${!field.enabled ? 'text-gray-400' : ''}`}>
                      {field.labelVi}
                    </span>
                    <button
                      onClick={() => toggleField('organisms', field.id)}
                      className={`ml-auto text-xs px-2 py-1 rounded ${
                        field.enabled ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {field.enabled ? '✓ Hiện' : '✗ Ẩn'}
                    </button>
                  </label>
                ))}
              </div>
            </div>

            {/* Squamous Cells */}
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <h3 className="font-bold text-red-800 mb-3">{t('cytology.squamousCellsTitle')}</h3>
              <div className="space-y-2">
                {config.squamousCells?.map(field => (
                  <label key={field.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={config.defaultSquamousCells[field.id] || false}
                      onChange={(e) => setConfig({
                        ...config,
                        defaultSquamousCells: { ...config.defaultSquamousCells, [field.id]: e.target.checked }
                      })}
                      disabled={!field.enabled}
                      className="w-4 h-4"
                    />
                    <span className={`text-sm ${!field.enabled ? 'text-gray-400' : ''}`}>
                      {field.labelVi}
                    </span>
                    <button
                      onClick={() => toggleField('squamousCells', field.id)}
                      className={`ml-auto text-xs px-2 py-1 rounded ${
                        field.enabled ? 'bg-red-200 text-red-800' : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {field.enabled ? '✓ Hiện' : '✗ Ẩn'}
                    </button>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Glandular Cells and Other Findings */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            {/* Glandular Cells */}
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h3 className="font-bold text-yellow-800 mb-3">{t('cytology.glandularCellsTitle')}</h3>
              <div className="space-y-2">
                {config.glandularCells?.map(field => (
                  <label key={field.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={config.defaultGlandularCells[field.id] || false}
                      onChange={(e) => setConfig({
                        ...config,
                        defaultGlandularCells: { ...config.defaultGlandularCells, [field.id]: e.target.checked }
                      })}
                      disabled={!field.enabled}
                      className="w-4 h-4"
                    />
                    <span className={`text-sm ${!field.enabled ? 'text-gray-400' : ''}`}>
                      {field.labelVi}
                    </span>
                    <button
                      onClick={() => toggleField('glandularCells', field.id)}
                      className={`ml-auto text-xs px-2 py-1 rounded ${
                        field.enabled ? 'bg-yellow-200 text-yellow-800' : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {field.enabled ? '✓ Hiện' : '✗ Ẩn'}
                    </button>
                  </label>
                ))}
              </div>
            </div>

            {/* Other Findings */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="font-bold text-blue-800 mb-3">{t('cytology.otherFindingsTitle')}</h3>
              <div className="space-y-2">
                {config.otherFindings?.map(field => (
                  <label key={field.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={config.defaultOtherFindings[field.id] || false}
                      onChange={(e) => setConfig({
                        ...config,
                        defaultOtherFindings: { ...config.defaultOtherFindings, [field.id]: e.target.checked }
                      })}
                      disabled={!field.enabled}
                      className="w-4 h-4"
                    />
                    <span className={`text-sm ${!field.enabled ? 'text-gray-400' : ''}`}>
                      {field.labelVi}
                    </span>
                    <button
                      onClick={() => toggleField('otherFindings', field.id)}
                      className={`ml-auto text-xs px-2 py-1 rounded ${
                        field.enabled ? 'bg-blue-200 text-blue-800' : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {field.enabled ? '✓ Hiện' : '✗ Ẩn'}
                    </button>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Conclusion */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">{t('cytology.conclusion')} (mặc định):</label>
            <textarea
              value={config.defaultConclusion}
              onChange={(e) => setConfig({ ...config, defaultConclusion: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          {/* Recommendation */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">{t('cytology.recommendation')} (mặc định):</label>
            <textarea
              value={config.defaultRecommendation}
              onChange={(e) => setConfig({ ...config, defaultRecommendation: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          {/* Footer Text */}
          <div>
            <label className="block text-sm font-medium mb-2">Footer Text:</label>
            <input
              type="text"
              value={config.footerText}
              onChange={(e) => setConfig({ ...config, footerText: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default CytologyTemplateEditor
